/**
 * `feature:explorer_databases_loaded` — how long a user waits between "the
 * connection is up" and "the Object Explorer shows the database list".
 *
 * Fires ONCE per connect (not per refresh): the measurement is anchored on the
 * ActiveConnection's `connectedAt`, so a later re-listing of the same
 * connection would report the age of the connection instead of a wait.
 */

import type { DatabaseEngineId } from '@/core/engines';
import { connectDurationBucket } from '@/core/analytics/telemetryBuckets';

/** Umami only accepts flat scalars — the same shape `electronAPI.analytics.track` takes. */
type AnalyticsProps = Record<string, string | number | boolean | null>;

/**
 * The `connectedAt` each connection has already reported for.
 *
 * Keyed on the TIMESTAMP, not just the id: a force Reconnect rebuilds the
 * connection in place — it never passes through 'disconnected', so no cleanup
 * runs — but it does replace `connectedAt`, and that new connect deserves its
 * own measurement. A plain refresh re-lists with the SAME `connectedAt` and
 * stays silent, which is the whole point of the latch.
 */
const reportedConnectedAt = new Map<string, number>();

/**
 * Drops a connection's latch. Memory hygiene for connections that are gone —
 * correctness no longer depends on it, because a new connect carries a new
 * `connectedAt`.
 */
export function forgetExplorerConnectLatency(connectionId: string): void {
  reportedConnectedAt.delete(connectionId);
}

/** Test seam — clears every latch. */
export function resetExplorerConnectLatency(): void {
  reportedConnectedAt.clear();
}

/**
 * Releases every latch for connections that are gone from the active set.
 *
 * Driven off the PREVIOUS active-connection ids, never off any home-preload
 * bookkeeping: a successful listing consumes that bookkeeping, so a version
 * that iterated it saw nothing to release on an ordinary disconnect. Since the
 * latch is keyed on `connectedAt` this is memory hygiene rather than the thing
 * that makes a reconnect report — but it is also where the caller's own
 * per-connection bookkeeping (the preload wave queue) is released, and that is
 * not self-expiring.
 *
 * @param alsoForget optional second latch to release for the same ids (the
 *   explorer's connect-time home-preload map).
 * @returns the ids that were released.
 */
export function forgetRemovedConnections(
  previousConnectionIds: Iterable<string>,
  currentConnectionIds: ReadonlySet<string>,
  alsoForget?: (connectionId: string) => void,
): string[] {
  const released: string[] = [];
  for (const connectionId of previousConnectionIds) {
    if (currentConnectionIds.has(connectionId)) continue;
    released.push(connectionId);
    forgetExplorerConnectLatency(connectionId);
    alsoForget?.(connectionId);
  }
  return released;
}

export interface ExplorerDatabasesLoadedInput {
  connectionId: string;
  /** Low-cardinality engine id; never a host, alias or database name. */
  engine?: DatabaseEngineId | string;
  /** `ActiveConnection.connectedAt`. Missing ⇒ nothing to measure, no event. */
  connectedAt?: Date | number;
  /** Test seam; defaults to `Date.now()`. */
  now?: number;
  /**
   * Test seam; defaults to the Umami bridge. Non-React context, so the default
   * is the fire-and-forget `window.electronAPI` call the analytics skill
   * prescribes outside components.
   */
  track?: (eventName: string, data?: AnalyticsProps) => void;
}

function defaultTrack(eventName: string, data?: AnalyticsProps): void {
  if (typeof window === 'undefined') return;
  window.electronAPI?.analytics?.track(eventName, data).catch(() => {});
}

/**
 * Reports the connect → database-list-rendered wait exactly once per connect.
 *
 * @returns `true` when an event was emitted.
 */
export function trackExplorerDatabasesLoaded({
  connectionId,
  engine,
  connectedAt,
  now,
  track = defaultTrack,
}: ExplorerDatabasesLoadedInput): boolean {
  if (!connectionId) return false;
  if (connectedAt === undefined || connectedAt === null) return false;

  const startedAt = connectedAt instanceof Date ? connectedAt.getTime() : connectedAt;
  if (!Number.isFinite(startedAt)) return false;
  if (reportedConnectedAt.get(connectionId) === startedAt) return false;

  reportedConnectedAt.set(connectionId, startedAt);
  track('feature:explorer_databases_loaded', {
    engine: engine ?? 'unknown',
    durationBucket: connectDurationBucket((now ?? Date.now()) - startedAt),
  });
  return true;
}
